<template>
  <v-dialog v-model="dialog" persistent max-width="50vw">
    <v-card flat>
      <v-toolbar flat color="#00BD73">
        <v-toolbar-title class="white--text font-weight-bold">
          {{ $t('carbonCredit.user.viewGeofence') || 'View Geofence' }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon class="white--text" @click="cancel">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="pa-4">
        <!-- MAP section -->
        <div
          class="google-map"
          style="width:100%;height:78vh; z-index: 0;"
          id="map"
          ref="googleMap"
        ></div>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="#00BD73" class="white--text font-weight-bold" @click="cancel" style="text-transform: uppercase;">
            {{ $t("close") || "CLOSE" }}
          </v-btn>
        </v-card-actions>
      </v-card-text>
    </v-card>


  </v-dialog>
</template>

<script>
import loading from "@/mixins/LoadingMixin";
import FarmService from "@/_services/FarmService";
import { getGoogleMapsLoader } from '@/mixins/GoogleMapLoaderSingleton';

export default {
  name: 'ViewGeofence',
  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    farmDetails: {
      type: Object,
      required: false,
      default: () => ({})
    },
    disableEditGeofence: {
      type: Boolean,
      required: false,
      default: true,
    },
    diligenceReportId: {
      type: [String, Number],
      required: false,
      default: 0,
    },
    coordinates: {
      type: Array,
      required: false,
      default: () => []
    },
    farmId: {
      type: [String, Number],
      required: false,
      default: null
    },
  },
  data() {
    return {
      geofenceType: "POLYGON",
      polygonPoints: 0,
      mapOptions: {
        mapTypeId: "satellite",
        center: { lat: 25.774, lng: -80.19 },
        styles: [
          {
            featureType: "all",
            elementType: "labels",
            stylers: [
              { visibility: "off" }
            ]
          }
        ],
        zoom: 18,
        disableDefaultUI: true,
        zoomControl: false,
      },
      markersArray: [],
      parentPolygonCoords: [],
      loader: null,
      latitude: 0,
      longitude: 0,
      google: null,
      map: null,
      marker: null,
      polygon: null,
      circle: null,
      geofenceRadius: 0,
      area: 0,

    };
  },

  watch: {
    dialog(newVal) {
      if (newVal && this.farmId && !this.farmDetails?.farm) {
        // Load farm data when dialog opens
        this.loadFarmData();
      } else if (newVal && this.farmDetails?.farm) {
        // If farmDetails already exists, ensure map is loaded and updated
        this.$nextTick(() => {
          if (!this.map) {
            this.loadGoogleMaps();
          } else {
            // Map exists, update it with segments data
            this.updateMapWithSegments();
          }
        });
      }
    },
    coordinates: {
      handler(newCoordinates) {
        if (newCoordinates && newCoordinates.length > 0 && this.map) {
          this.updateMapWithCoordinates(newCoordinates);
        }
      },
      deep: true
    }
  },
  async mounted() {
    // If farmId is provided but no farmDetails, load farm data
    if (this.farmId && !this.farmDetails?.farm) {
      await this.loadFarmData();
      return;
    }
    
    // Wait for DOM to be ready before loading Google Maps
    await this.$nextTick();
    await this.loadGoogleMaps();
  },
  methods: {
    async loadFarmData() {
      if (!this.farmId) return;
      
      try {
        const farmData = await FarmService.getFarm(this.farmId);
        if (farmData) {
          // Extract geofence data and set up farmDetails structure
          const { coordinates, geofenceType } = this.extractGeofenceData(farmData);
          
          // Set up farmDetails in the format expected by the component
          this.farmDetails = {
            farm: farmData,
            farmType: geofenceType
          };
          
          // Update geofence type
          this.geofenceType = geofenceType;
          
          // Wait for map to be ready, then update
          if (this.map) {
            // Map already loaded, update it
            if (coordinates && coordinates.length > 0) {
              this.updateMapWithCoordinates(coordinates, geofenceType);
            } else {
              // Use segments data directly
              this.updateMapWithSegments();
            }
          } else {
            // Map not loaded yet, wait for it
            await this.$nextTick();
            await this.loadGoogleMaps();
          }
        }
      } catch (error) {
        console.error('Error loading farm data:', error);
        this.$toast?.error('Failed to load farm geofence data');
      }
    },
    
    extractGeofenceData(farm) {
      let coordinates = [];
      let geofenceType = 'POLYGON';

      // Check for segments data first
      if (farm.segments && farm.segments.length > 0) {
        const segment = farm.segments[0];
        
        // Check if it's a circular geofence (POINT)
        if (segment.geofenceRadius && segment.geofenceCenterLat && segment.geofenceCenterLog) {
          geofenceType = 'POINT';
          // Note: API returns geofenceCenterLat as longitude and geofenceCenterLog as latitude (swapped)
          // Swap them to get correct lat/lng
          coordinates = [{
            lat: parseFloat(segment.geofenceCenterLog), // Swap: Log is actually latitude
            log: parseFloat(segment.geofenceCenterLat), // Swap: Lat is actually longitude
            radius: parseFloat(segment.geofenceRadius)
          }];
          this.area = parseFloat(segment.geofenceArea || 0);
        } 
        // Check if it's a polygon geofence
        else if (segment.coordinates && segment.coordinates.length > 0) {
          geofenceType = 'POLYGON';
          coordinates = segment.coordinates.map(coord => ({
            lat: parseFloat(coord.lat),
            log: parseFloat(coord.log)
          }));
          this.area = parseFloat(segment.geofenceArea || 0);
        }
      }
      
      // Check for GeoFences (circular geofence)
      if (coordinates.length === 0 && farm.GeoFences && farm.GeoFences.length > 0) {
        const geoFence = farm.GeoFences[0];
        if (geoFence.geofenceCenterLat && geoFence.geofenceCenterLog && geoFence.geofenceRadius) {
          geofenceType = 'POINT';
          // Note: API returns geofenceCenterLat as longitude and geofenceCenterLog as latitude (swapped)
          // Swap them to get correct lat/lng
          coordinates = [{
            lat: parseFloat(geoFence.geofenceCenterLog), // Swap: Log is actually latitude
            log: parseFloat(geoFence.geofenceCenterLat), // Swap: Lat is actually longitude
            radius: parseFloat(geoFence.geofenceRadius)
          }];
        }
      }
      
      // Check for coordinates array (polygon geofence)
      if (coordinates.length === 0 && farm.coordinates && Array.isArray(farm.coordinates) && farm.coordinates.length > 0) {
        geofenceType = 'POLYGON';
        coordinates = farm.coordinates.map(coord => ({
          lat: parseFloat(coord.lat),
          log: parseFloat(coord.log)
        }));
      }
      
      // Check for FarmCoordinates
      if (coordinates.length === 0 && farm.FarmCoordinates && Array.isArray(farm.FarmCoordinates) && farm.FarmCoordinates.length > 0) {
        geofenceType = 'POLYGON';
        coordinates = farm.FarmCoordinates.map(coord => ({
          lat: parseFloat(coord.lat),
          log: parseFloat(coord.log)
        }));
      }
      
      // Fallback: use lat/log if available (for simple point location)
      if (coordinates.length === 0 && farm.lat && farm.log) {
        const lat = parseFloat(farm.lat);
        const log = parseFloat(farm.log);
        if (!isNaN(lat) && !isNaN(log)) {
          geofenceType = 'POLYGON';
          coordinates = [{ lat, log }];
        }
      }

      return { coordinates, geofenceType };
    },
    
    updateMapWithCoordinates(coordinates) {
      if (!coordinates || coordinates.length === 0) return;
      
      // Clear existing polygon and circle
      if (this.polygon) {
        this.polygon.setMap(null);
        this.polygon = null;
      }
      if (this.circle) {
        this.circle.setMap(null);
        this.circle = null;
      }
      
      // Clear existing markers
      this.removeExistingMarkers();
      
      if (this.farmDetails?.farmType?.toUpperCase() === "POINT") {
        // Handle circular geofence from segments data
        if (this.farmDetails?.farm?.segments && this.farmDetails.farm.segments.length > 0) {
          const segment = this.farmDetails.farm.segments[0];
          // Note: API returns geofenceCenterLat as longitude and geofenceCenterLog as latitude (swapped)
          // Swap them to get correct lat/lng
          this.latitude = parseFloat(segment.geofenceCenterLog); // Swap: Log is actually latitude
          this.longitude = parseFloat(segment.geofenceCenterLat); // Swap: Lat is actually longitude
          this.geofenceRadius = parseFloat(segment.geofenceRadius);
          this.area = parseFloat(segment.geofenceArea);
        } else if (coordinates.length === 1) {
          // Fallback to coordinates prop
          const coord = coordinates[0];
          this.latitude = parseFloat(coord["lat"]);
          this.longitude = parseFloat(coord["log"]);
          this.geofenceRadius = parseFloat(coord["radius"] || 0);
        }
        
        // Update map center
        this.mapOptions.center = {
          lat: this.latitude,
          lng: this.longitude,
        };
          this.map.setCenter(this.mapOptions.center);
          this.map.setZoom(18);
        
        // Create circle and marker
        this.createCircle(this.geofenceRadius);
        this.createMarker();
      } else {
        // Handle polygon geofence
        const codes = coordinates.map(function(elm) {
          return {
            lat: parseFloat(elm["lat"]),
            lng: parseFloat(elm["log"]),
          };
        });
        
        this.parentPolygonCoords = codes;
        
        // Update map center to first coordinate
        if (this.parentPolygonCoords.length > 0) {
          const { lat, lng } = this.parentPolygonCoords[0];
          this.mapOptions.center = { lat, lng };
          this.map.setCenter({ lat, lng });
          this.map.setZoom(18);
        }
        
        // Create new polygon and markers
        this.createPolygon();
      }
    },
    
    updateMapWithSegments() {
      if (!this.farmDetails?.farm?.segments || this.farmDetails.farm.segments.length === 0) {
        return;
      }
      
      const segment = this.farmDetails.farm.segments[0];
      
      // Clear existing polygon and circle
      if (this.polygon) {
        this.polygon.setMap(null);
        this.polygon = null;
      }
      if (this.circle) {
        this.circle.setMap(null);
        this.circle = null;
      }
      
      // Clear existing markers
      this.removeExistingMarkers();
      
      if (this.farmDetails?.farmType?.toUpperCase() === "POINT" && 
          segment.geofenceCenterLat && segment.geofenceCenterLog && segment.geofenceRadius) {
        // Handle circular geofence
        // Note: API returns geofenceCenterLat as longitude and geofenceCenterLog as latitude (swapped)
        // Swap them to get correct lat/lng
        this.latitude = parseFloat(segment.geofenceCenterLog); // Swap: Log is actually latitude
        this.longitude = parseFloat(segment.geofenceCenterLat); // Swap: Lat is actually longitude
        this.geofenceRadius = parseFloat(segment.geofenceRadius);
        this.area = parseFloat(segment.geofenceArea);
        
        // Update map center
        const center = {
          lat: this.latitude,
          lng: this.longitude,
        };
        
        this.mapOptions.center = center;
        if (this.map) {
          this.map.setCenter(center);
          this.map.setZoom(18);
        }
        
        // Create circle and marker
        this.createCircle(this.geofenceRadius);
        this.createMarker();
      } else if (this.farmDetails?.farmType?.toUpperCase() === "POLYGON" && 
                 segment.coordinates && segment.coordinates.length > 0) {
        // Handle polygon geofence
        const codes = segment.coordinates.map(function(elm) {
          return {
            lat: parseFloat(elm["lat"]),
            lng: parseFloat(elm["log"]),
          };
        });
        
        this.parentPolygonCoords = codes;
        this.area = parseFloat(segment.geofenceArea);
        
        // Update map center to first coordinate
        if (this.parentPolygonCoords.length > 0) {
          const { lat, lng } = this.parentPolygonCoords[0];
          this.mapOptions.center = { lat, lng };
          if (this.map) {
            this.map.setCenter({ lat, lng });
            this.map.setZoom(18);
          }
        }
        
        // Create polygon and markers
        this.createPolygon();
      }
    },
    

    async loadGoogleMaps() {
      try {
        // Ensure the map container exists before proceeding
        if (!this.$refs.googleMap) {
          await this.$nextTick();
          if (!this.$refs.googleMap) {
            return;
          }
        }

        const loader = getGoogleMapsLoader();
        const google = await loader.load();
        await this.initializeMap(google);  
        
        // After map is initialized, update with coordinates
        if (this.coordinates && this.coordinates.length > 0) {
          this.updateMapWithCoordinates(this.coordinates);
        } else if (this.farmDetails?.farm?.segments && this.farmDetails.farm.segments.length > 0) {
          // If no coordinates prop but segments exist, update with segments data
          this.updateMapWithSegments();
        }
        
        this.loader = loader;
      } catch (e) {
        // Handle error silently
      }
    },
    async initializeMap(google) {
      this.google = google;
      const mapContainer = this.$refs.googleMap;

      // Check if the map container exists
      if (!mapContainer) {
        console.error("Map container not found. Waiting for DOM to be ready...");
        // Wait for next tick to ensure DOM is ready
        await this.$nextTick();
        const retryContainer = this.$refs.googleMap;
        if (!retryContainer) {
          console.error("Map container still not found after waiting");
          return;
        }
      }

      this.map = await new google.maps.Map(mapContainer, this.mapOptions);
      
      if (!this.disableEditGeofence) {
        this.map.addListener("click", async (mapsMouseEvent) => {
          const latLng = mapsMouseEvent.latLng.toJSON();
          this.parentPolygonCoords.push(latLng);
          const geocoder = new this.google.maps.Geocoder();
          geocoder
            .geocode({ location: latLng })
            .then((response) => {
              if (response.results[0]) {
                this.latitude = latLng.lat;
                this.longitude = latLng.lng;
                this.address = response.results[0].formatted_address;
                this.createMarker();
              } else {
                window.alert("No results found");
              }
            })
            .catch((e) => window.alert("Can't fetch location due to: " + e));
        });
      }

      this.createPolygon();
      this.createMarker();
    },

    createPolygon() {
      if (this.polygon != null) this.polygon.setMap(null);
      this.polygon = new this.google.maps.Polygon({
        paths: this.parentPolygonCoords,
        strokeColor: "#1F5D51",
        strokeOpacity: 0.8,
        strokeWeight: 3,
        fillColor: "#E5F8F1",
        fillOpacity: 0.7,
        editable: !this.disableEditGeofence,
        draggable: !this.disableEditGeofence,
        icon: this.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
      });
      this.createMarkerArray(this.parentPolygonCoords);
      this.polygon.setMap(this.map);
    },
    async createCircle(radius = 0) {
      this.circle = await new this.google.maps.Circle({
        fillColor: "#FFFFFF",
        fillOpacity: 0.2,
        strokeWeight: 0,
        map: this.map,
        center: {
          lat: parseFloat(this.latitude),
          lng: parseFloat(this.longitude),
        },
        radius,
      });
      this.circle.setMap(this.map);
    },
    createMarker() {
      if (this.marker != null) this.marker.setMap(null);
      const latLng = { lat: parseFloat(this.latitude), lng: parseFloat(this.longitude) };
      this.marker = new this.google.maps.Marker({
        position: latLng,
      });

      // To add the marker to the map, call setMap();
      this.marker.setMap(this.map);
    },
    createMarkerArray(latLngArray) {
      this.removeExistingMarkers();
      const svgMarker = {
        path: "M-20,0a20,20 0 1,0 40,0a20,20 0 1,0 -40,0",
        fillColor: "#00BD73",
        fillOpacity: 1,
        strokeWeight: 0,
        rotation: 0,
        scale: 0.4,
        anchor: new this.google.maps.Point(0, 0),
      };
      const svgMarkerRed = {
        path: "M-20,0a20,20 0 1,0 40,0a20,20 0 1,0 -40,0",
        fillColor: "red",
        fillOpacity: 1,
        strokeWeight: 0,
        rotation: 0,
        scale: 0.4,
        anchor: new this.google.maps.Point(0, 0),
      };
      latLngArray.forEach((latLng, i) => {
        this.markersArray[i] = new this.google.maps.Marker({
          position: latLng,
          map: this.map,
          icon:
            this.parentPolygonCoords.length - 1 == i ? svgMarkerRed : svgMarker,
          draggable: !this.disableEditGeofence,
        });

        if (!this.disableEditGeofence) {
          this.markersArray[i].addListener("click", () => {
            let firstIndex = i + 1;
            this.parentPolygonCoords = this.reArrangeArray(
              firstIndex,
              this.parentPolygonCoords
            );
            this.createMarkerArray(this.parentPolygonCoords);
          });
          this.markersArray[i].addListener("dragend", () => {
            var lat = this.markersArray[i].getPosition().lat();
            var lng = this.markersArray[i].getPosition().lng();
            this.parentPolygonCoords[i] = { lat, lng };
            this.createPolygon();
          });
        }
      });
    },

    removeExistingMarkers() {
      for (let i = 0; i < this.markersArray.length; i++) {
        this.markersArray[i].setMap(null);
      }
      this.markersArray = [];
    },

    cancel() {
      this.$emit("close");
    },
  },
  mixins: [loading],
};
</script>

<style lang="scss" scoped>
.google-map {
  border-radius: 4px;
}
</style>
